import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Carousel } from '@johnlewispartnership/wtr-ingredients/ingredients/Carousel';
import { ProductPod } from 'components/ProductPod2';

import { makeRecommendedCarouselContext } from 'utils/clickContext/recommendedCarouselContext';
import styles from './RecommendedCarousel.scss';
import { useRecommendedCarousel } from './useRecommendedCarousel';
import { useCarouselGA } from './useCarouselGA';

const recommendedCarouselDefaultPropProducts = [];

export default function RecommendedCarousel({
  titleDesktop,
  titleMobileAndTablet,
  onSlideView,
  subTitle,
  products = recommendedCarouselDefaultPropProducts,
  className,
  ariaName,
  nameForAnalytics,
  typeForAnalytics,
  backgroundColor,
  buttonsTheme = 'dark',
  divider = false,
  linkData,
  list,
  image,
  podVariant = 'light',
  experience,
  listDefinitions,
}) {
  const { title, productFindingMethod, linkClick, carouselRef } = useRecommendedCarousel({
    products,
    titleDesktop,
    titleMobileAndTablet,
    linkData,
    experience,
  });

  const clickContext = {
    ...makeRecommendedCarouselContext({
      name: nameForAnalytics || ariaName || titleDesktop,
      type: typeForAnalytics,
    }),
    productFindingMethod,
    listDefinitions: { ...listDefinitions, title },
  };

  useCarouselGA({ list, products });

  return (
    <div
      ref={carouselRef}
      className={classNames(className, styles.recommendedCarousel)}
      data-testid="rec-prods"
    >
      <Carousel
        ariaName={ariaName}
        backgroundColor={backgroundColor}
        title={title}
        buttonsTheme={buttonsTheme}
        divider={divider}
        subTitle={subTitle}
        linkData={{ ...linkData, onClick: linkClick }}
        onSlideView={onSlideView}
        image={image}
        disableScrollTo
      >
        {products.map(({ searchProduct, sponsored }, i) => (
          <ProductPod
            key={searchProduct}
            lineNumber={searchProduct}
            position={i + 1}
            sponsored={sponsored}
            clickContext={clickContext}
            imageLoading={i >= 4 ? 'lazy' : null}
            variant={podVariant}
            listDefinitions={{ ...listDefinitions, title }}
          />
        ))}
      </Carousel>
    </div>
  );
}

RecommendedCarousel.displayName = 'RecommendedCarousel';

RecommendedCarousel.propTypes = {
  titleDesktop: PropTypes.string.isRequired,
  titleMobileAndTablet: PropTypes.string.isRequired,
  onSlideView: PropTypes.func,
  subTitle: PropTypes.string,
  ariaName: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      searchProduct: PropTypes.string,
      position: PropTypes.number,
      sponsored: PropTypes.bool,
    }),
  ),
  className: PropTypes.string,
  nameForAnalytics: PropTypes.string,
  typeForAnalytics: PropTypes.string,
  backgroundColor: PropTypes.string,
  buttonsTheme: PropTypes.oneOf(['dark', 'light']),
  divider: PropTypes.bool,
  linkData: PropTypes.shape({
    text: PropTypes.string,
    url: PropTypes.string,
    newWindow: PropTypes.bool,
    type: PropTypes.oneOf(['link', 'button']),
  }),
  image: PropTypes.shape({
    imageUrl: PropTypes.string,
    imageAlt: PropTypes.string,
  }),
  podVariant: PropTypes.oneOf(['light', 'regular', 'slim']),
};
